import { useState } from 'react'
import { format } from 'date-fns'

import Modal from '../Modal'
import Button from '../Button'
import {
  ACTIONS,
  KIND,
  METRIC_LABELS,
  actionLabel,
  controlOf,
  currentSalt,
  flagValidationErrors,
  formatNumber,
  formatPercent,
  isResultStale,
  suggestNextSalt,
  variantKeys
} from '../../helper/experiments'
import { LockNotice, Notice } from './ExperimentBadges'
import { Explain, HELP } from './Help'

const inputClass =
  'block w-full px-2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'

const CHECKED_ACTIONS = [ACTIONS.START, ACTIONS.RESUME, ACTIONS.RESTART]

const formatDate = (value) => {
  const date = value ? new Date(value) : null
  return date && !Number.isNaN(date.getTime())
    ? format(date, 'dd.MM.yyyy')
    : '-'
}

const DecisionSummary = ({ decision = {} }) => (
  <ul className='mt-2 list-inside list-disc space-y-1 text-sm text-gray-700'>
    <li>
      Primäre Metrik {METRIC_LABELS[decision.primary] || decision.primary}
    </li>
    <li>
      Laufzeit {formatNumber(decision.min_days)} bis{' '}
      {formatNumber(decision.max_days)} Tage
    </li>
    <li>
      Mindestens {formatNumber(decision.min_exposures_per_variant)} Exposures je
      Variante und {formatNumber(decision.min_conversions_total)} Conversions
      insgesamt
    </li>
    <li>Gewinner ab P(best) {formatPercent(decision.threshold, 1)}</li>
  </ul>
)

const WinnerPicker = ({ flag, winner, setWinner, suggested }) => (
  <fieldset className='mt-4'>
    <legend className='text-xs font-medium text-gray-700'>Gewinner</legend>
    <div className='mt-2 space-y-2'>
      {variantKeys(flag).map((key) => (
        <label
          key={key}
          className='flex items-center gap-2 text-sm text-gray-700'
        >
          <input
            type='radio'
            name='winner'
            value={key}
            checked={winner === key}
            onChange={() => setWinner(key)}
            className='h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600'
          />
          <span className='font-mono'>{key}</span>
          {key === controlOf(flag) && flag.kind === KIND.EXPERIMENT && (
            <span className='text-xs text-gray-400'>Control</span>
          )}
          {key === suggested && (
            <span className='text-xs text-green-700'>
              Vorschlag des Evaluators
            </span>
          )}
        </label>
      ))}
    </div>
  </fieldset>
)

const ActionBody = ({
  action,
  flag,
  result,
  onConfirm,
  onCancel,
  isSaving
}) => {
  const isExperiment = flag.kind === KIND.EXPERIMENT
  const keys = variantKeys(flag)
  // Kein Vorschlag aus einem Lauf vor dem aktuellen Start
  const suggested =
    !isResultStale(flag, result) && keys.includes(result?.decision?.winner)
      ? result.decision.winner
      : null

  const [winner, setWinner] = useState(
    () => suggested || (isExperiment ? keys[0] : 'on')
  )
  const [salt, setSalt] = useState(() => suggestNextSalt(flag))

  const problems = CHECKED_ACTIONS.includes(action)
    ? flagValidationErrors(flag)
    : []
  const needsSalt = action === ACTIONS.RESTART && isExperiment
  const saltInvalid =
    needsSalt && (!salt.trim() || salt.trim() === currentSalt(flag))

  const title = `${isExperiment ? 'Experiment' : 'Rollout'} ${actionLabel(action, flag).toLowerCase()}`

  const confirm = () =>
    onConfirm({
      winner: action === ACTIONS.CONCLUDE ? winner : undefined,
      salt: needsSalt ? salt : undefined
    })

  return (
    <div>
      <h3 className='text-base font-semibold leading-6 text-gray-900'>
        {title}
      </h3>
      <p className='mt-1 font-mono text-sm text-gray-500'>{flag.key}</p>

      <div className='mt-4 space-y-3 text-sm text-gray-700'>
        {action === ACTIONS.START && isExperiment && (
          <>
            <p>
              Ab jetzt wird zugeteilt und jede Zufallszuteilung zählt als
              Exposure. Varianten, Gewichte, Salt und Entscheidungskriterien
              sind ab dem Start gesperrt.
            </p>
            <DecisionSummary decision={flag.decision} />
            <p>
              Am nächsten Morgen gegen 07:00 UTC kommt die erste Auswertung, in
              Slack eine Startmeldung. Den Test danach einfach laufen lassen,
              der Evaluator beendet ihn selbst.
            </p>
            {flag.unit !== 'visitor' && (
              <Notice tone='info'>
                Unit ist {flag.unit}. Der Evaluator wertet nur Unit visitor aus,
                hier bleibt die Auswertung manuell.
              </Notice>
            )}
          </>
        )}

        {action === ACTIONS.START && !isExperiment && (
          <p>
            Ab jetzt bekommen {formatNumber(flag.percent)} % der Units die
            Variante on.
          </p>
        )}

        {action === ACTIONS.PAUSE && (
          <p>
            {isExperiment
              ? 'Solange pausiert ist, bekommen alle Control und es zählen keine Exposures. Die Kriterien bleiben gesperrt.'
              : 'Solange pausiert ist, bekommen alle off.'}
          </p>
        )}

        {action === ACTIONS.RESUME && (
          <p>
            Die Zuteilung läuft weiter wie vorher. Die Laufzeit zählt ab dem
            ursprünglichen Start am {formatDate(flag.started_at)}.
          </p>
        )}

        {action === ACTIONS.CONCLUDE && (
          <>
            <p>
              {isExperiment
                ? 'Danach bekommen alle die gewählte Variante. Den Code der anderen Varianten entfernst du anschließend von Hand. Normalerweise beendet der Evaluator den Test selbst, von Hand nur, wenn etwas schiefläuft oder die Variante Schaden anrichtet.'
                : 'Danach gilt die gewählte Variante für alle. on heißt alle bekommen das Feature, off heißt niemand.'}
            </p>
            <WinnerPicker
              flag={flag}
              winner={winner}
              setWinner={setWinner}
              suggested={suggested}
            />
          </>
        )}

        {action === ACTIONS.RESTART && isExperiment && (
          <>
            <p>
              Ein neuer Salt ergibt eine neue Verteilung. Die Laufzeit beginnt
              neu, winner und concluded_at werden geleert. Prüf vorher Varianten
              und Kriterien, nach dem Start sind sie wieder gesperrt.
            </p>
            <DecisionSummary decision={flag.decision} />
            <div>
              <label
                htmlFor='restart-salt'
                className='block text-xs font-medium text-gray-700 mb-1'
              >
                Neuer Salt
              </label>
              <input
                id='restart-salt'
                className={`${inputClass} font-mono`}
                value={salt}
                onChange={(e) => setSalt(e.target.value)}
              />
              {saltInvalid && (
                <p className='mt-1 text-xs text-red-600'>
                  Der Salt muss sich vom bisherigen ({currentSalt(flag)})
                  unterscheiden.
                </p>
              )}
            </div>
            <Explain>{HELP.salt}</Explain>
            <LockNotice />
          </>
        )}

        {action === ACTIONS.RESTART && !isExperiment && (
          <p>
            Der Rollout teilt wieder nach Prozent zu (
            {formatNumber(flag.percent)} %). Der Gewinner wird geleert.
          </p>
        )}

        {problems.length > 0 && (
          <Notice tone='error'>
            <p className='font-medium'>
              Das Flag ist noch nicht vollständig. Bitte zuerst bearbeiten.
            </p>
            <ul className='mt-1 list-inside list-disc'>
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Notice>
        )}
      </div>

      <div className='mt-6 flex justify-end gap-3'>
        <Button.SecondaryLG
          text='Abbrechen'
          onClick={onCancel}
          disabled={isSaving}
        />
        <Button.PrimaryLG
          text={actionLabel(action, flag)}
          onClick={confirm}
          isLoading={isSaving}
          disabled={problems.length > 0 || saltInvalid}
        />
      </div>
    </div>
  )
}

// `pending` = { id, action, flag, result }. Bleibt nach dem Schließen
// erhalten, damit der Inhalt während der Ausblend-Animation stehen bleibt.
const StatusActionModal = ({ open, setOpen, pending, onConfirm, isSaving }) => (
  <Modal open={open} setOpen={setOpen} size='lg' disableClose={isSaving}>
    {pending && (
      <ActionBody
        key={pending.id}
        action={pending.action}
        flag={pending.flag}
        result={pending.result}
        onConfirm={onConfirm}
        onCancel={() => setOpen(false)}
        isSaving={isSaving}
      />
    )}
  </Modal>
)

export default StatusActionModal
